import { defineStore } from 'pinia'
import { encryptedStorage } from '@/utils/encryption'

/**
 * Cache verilerini saklamak için Pinia store
 * Kalıcı olarak veriler saklanır ve sayfa yenilemelerinde korunur
 */
export const useCacheStore = defineStore('cache', {
  state: () => ({
    storage: {}, // Cache verileri burada saklanır
    createdAt: Date.now(), // Store oluşturulma zamanı
    version: 1 // Cache versiyonu (ileride şema değişikliği için)
  }),

  actions: {
    /**
     * Cache'e veri ekler
     * @param {string} key - Cache anahtarı
     * @param {any} value - Cache'lenecek veri
     * @param {Object} options - Cache seçenekleri
     */
    set(key, value, options = {}) {
      const ttl = options.ttl || null;
      const expiry = ttl ? Date.now() + ttl : null;
      
      this.storage[key] = {
        value,
        expiry,
        createdAt: Date.now(),
        metadata: options.metadata || {}
      };
      
      // Persist işlemini manuel tetikle
      this.$persist();
    },
    
    /**
     * Cache'den veri alır
     * @param {string} key - Cache anahtarı
     * @returns {any|null} Cache değeri veya null (bulunamazsa/süresi dolmuşsa)
     */
    get(key) {
      const item = this.storage[key];
      
      if (!item) {
        return null;
      }
      
      // TTL kontrolü
      if (item.expiry && item.expiry < Date.now()) {
        this.delete(key);
        return null;
      }
      
      return item.value;
    },
    
    /**
     * Cache'den değeri ve metadata bilgisini alır
     * @param {string} key - Cache anahtarı
     * @returns {Object|null} {value, metadata} veya null
     */
    getWithMetadata(key) {
      const item = this.storage[key];
      
      if (!item) {
        return null;
      }
      
      // TTL kontrolü
      if (item.expiry && item.expiry < Date.now()) {
        this.delete(key);
        return null;
      }
      
      return {
        value: item.value,
        createdAt: item.createdAt,
        expiry: item.expiry,
        metadata: item.metadata
      };
    },
    
    /**
     * Cache'den değer siler
     * @param {string} key - Cache anahtarı
     */
    delete(key) {
      delete this.storage[key];
      // Persist işlemini manuel tetikle
      this.$persist();
    },
    
    /**
     * Desene uyan tüm keyleri siler
     * @param {RegExp} pattern - Silinecek keylerin deseni
     */
    deleteByPattern(pattern) {
      let deleted = false;
      Object.keys(this.storage).forEach(key => {
        if (pattern.test(key)) {
          delete this.storage[key];
          deleted = true;
        }
      });
      
      // Eğer herhangi bir key silindiyse persist işlemini tetikle
      if (deleted) {
        this.$persist();
      }
    },
    
    /**
     * Tüm cache'i temizler
     */
    clear() {
      this.storage = {};
      // Persist işlemini manuel tetikle
      this.$persist();
    },
    
    /**
     * Süresi dolmuş cache öğelerini temizler
     */
    cleanup() {
      const now = Date.now();
      let cleaned = false;
      
      Object.entries(this.storage).forEach(([key, item]) => {
        if (item.expiry && item.expiry < now) {
          delete this.storage[key];
          cleaned = true;
        }
      });
      
      // Eğer herhangi bir item temizlendiyse persist işlemini tetikle
      if (cleaned) {
        this.$persist();
      }
      
      return cleaned;
    },
    
    /**
     * Cache içindeki tüm anahtarları döndürür
     * @returns {string[]} Cache anahtarları
     */
    keys() {
      return Object.keys(this.storage);
    },
    
    /**
     * Cache içindeki entry sayısını döndürür
     * @returns {number} Cache'deki eleman sayısı
     */
    size() {
      return Object.keys(this.storage).length;
    },
    
    /**
     * Cache'nin kullandığı tahmini bellek boyutunu döndürür
     * @returns {number} Yaklaşık byte sayısı
     */
    estimatedSize() {
      let size = 0;
      
      Object.entries(this.storage).forEach(([key, item]) => {
        // Anahtar boyutu
        size += key.length * 2;
        
        // Değer boyutu (yaklaşık)
        try {
          const valueString = JSON.stringify(item.value);
          size += valueString.length * 2;
        } catch {
          // JSON'a çevrilemezse tahmini değer
          size += 1000;
        }
        
        // Metadata ve zaman damgası
        size += 100;
      });
      
      return size;
    },
    
    /**
     * Periyodik temizlik işlevi - düzenli aralıklarla çağrılmalı
     */
    performPeriodicCleanup() {
      this.cleanup();
    },
    
    /**
     * Persist işlemini manuel olarak tetikler
     */
    $persist() {
      if (typeof this.$persistNow === 'function') {
        this.$persistNow();
      }
    }
  },

  // Kalıcı store - sayfa yenilemesi ve tarayıcı kapatılması arasında korunur
  persist: {
    key: 'lobster-cache-v1',
    storage: encryptedStorage,
    // Tüm state'i persist et
    paths: ['storage', 'createdAt', 'version'],
    // Restore öncesi yapılacak işlemler
    beforeRestore: (context) => {
      console.log('Cache store restoring from storage...', context.store.storage ? Object.keys(context.store.storage).length : 0, 'items');
    },
    // Restore sonrası yapılacak işlemler
    afterRestore: (context) => {
      // Restore sonrası temizlik
      const store = context.store;
      const cleaned = store.cleanup();
      console.log('Cache store restored and cleaned up:', cleaned ? 'expired items removed' : 'no expired items');
    }
  }
});